import { FC, useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import { useSnackbar } from "notistack";
import { Box } from "@mui/material";
import Carousel from "../Carousel";
import { Cryptocurrency } from "../../types/assets";
import CryptoOfferingCoin from "../CryptoOfferingCoin";
import { useAppDispatch, useAppSelector } from "../../hooks/useStore";
import {
  fetchTokenPriceIndexEOD,
  selectTokenPriceIndexEOD,
} from "../../redux/slices/indexes";
import { selectAnalyzerToken } from "../../redux/slices/auth";
import { MultiCalc } from "../../types/analyzer";
import { getMultiCalc, getMultiCalcLegacy } from "../../services/analyzer";

import ImgAave from "../../assets/images/products/crypto-offering/feeds/aave.svg";
import ImgAda from "../../assets/images/products/crypto-offering/feeds/ada.svg";
import ImgAxs from "../../assets/images/products/crypto-offering/feeds/axs.svg";
import ImgBnb from "../../assets/images/products/crypto-offering/feeds/bnb.svg";
import ImgBtc from "../../assets/images/products/crypto-offering/feeds/btc.svg";
import ImgChain from "../../assets/images/products/crypto-offering/feeds/chain.svg";
import ImgDoge from "../../assets/images/products/crypto-offering/feeds/doge.svg";
import ImgDot from "../../assets/images/products/crypto-offering/feeds/dot.svg";
import ImgEos from "../../assets/images/products/crypto-offering/feeds/eos.svg";
import ImgEth from "../../assets/images/products/crypto-offering/feeds/eth.svg";
import ImgFil from "../../assets/images/products/crypto-offering/feeds/fil.svg";
import ImgInter from "../../assets/images/products/crypto-offering/feeds/inter.svg";
import ImgLink from "../../assets/images/products/crypto-offering/feeds/link.svg";
import ImgLuna from "../../assets/images/products/crypto-offering/feeds/luna.svg";
import ImgLtc from "../../assets/images/products/crypto-offering/feeds/ltc.svg";
import ImgPol from "../../assets/images/products/crypto-offering/feeds/pol.svg";
import ImgSol from "../../assets/images/products/crypto-offering/feeds/sol.svg";
import ImgStl from "../../assets/images/products/crypto-offering/feeds/stl.svg";
import ImgTheta from "../../assets/images/products/crypto-offering/feeds/theta.svg";
import ImgTxr from "../../assets/images/products/crypto-offering/feeds/txr.svg";
import ImgUni from "../../assets/images/products/crypto-offering/feeds/uni.svg";
import ImgXmr from "../../assets/images/products/crypto-offering/feeds/xmr.svg";

interface Props {
  cryptocurrencies: Cryptocurrency[];
}

const DATA = [
  {
    id: "ADA",
    label: "",
    image: ImgAda,
  },
  {
    id: "AAVE",
    label: "",
    image: ImgAave,
  },
  {
    id: "AXS",
    label: "",
    image: ImgAxs,
  },
  {
    id: "BNB",
    label: "",
    image: ImgBnb,
  },
  {
    id: "BTC",
    label: "",
    image: ImgBtc,
  },
  {
    id: "CHAIN",
    label: "",
    image: ImgChain,
  },
  {
    id: "DOGE",
    label: "",
    image: ImgDoge,
  },
  {
    id: "DOT",
    label: "",
    image: ImgDot,
  },
  {
    id: "EOS",
    label: "",
    image: ImgEos,
  },
  {
    id: "ETH",
    label: "",
    image: ImgEth,
  },
  {
    id: "FIL",
    label: "",
    image: ImgFil,
  },
  {
    id: "INTER",
    label: "",
    image: ImgInter,
  },
  {
    id: "LINK",
    label: "",
    image: ImgLink,
  },
  {
    id: "LUNA",
    label: "",
    image: ImgLuna,
  },
  {
    id: "LTC",
    label: "",
    image: ImgLtc,
  },
  {
    id: "POL",
    label: "",
    image: ImgPol,
  },
  {
    id: "SOL",
    label: "",
    image: ImgSol,
  },
  {
    id: "STL",
    label: "",
    image: ImgStl,
  },
  {
    id: "THETA",
    label: "",
    image: ImgTheta,
  },
  {
    id: "TXR",
    label: "",
    image: ImgTxr,
  },
  {
    id: "UNI",
    label: "",
    image: ImgUni,
  },
  {
    id: "XMR",
    label: "",
    image: ImgXmr,
  },
];

const CryptoFeeds: FC<Props> = ({ cryptocurrencies }) => {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const analyzerToken = useAppSelector(selectAnalyzerToken);
  const tokenPriceEod = useAppSelector(selectTokenPriceIndexEOD);

  const [selected, setSelected] = useState<Cryptocurrency>();
  const [data, setData] = useState<MultiCalc[]>([]);
  const [error, setError] = useState<any>(null);

  const calcInput = useMemo(() => {
    if (!selected || !tokenPriceEod[selected.symbol]) {
      return {};
    }

    return tokenPriceEod[selected.symbol].reduce((acc, item) => {
      const { timestamp, value } = item;
      return { ...acc, [moment(timestamp).format("yyyy-MM-DD")]: value };
    }, {});
  }, [selected, tokenPriceEod]);

  const items: (Cryptocurrency & { image?: string })[] = useMemo(() => {
    return cryptocurrencies
      .filter((c) => !!c.description)
      .map((c) => {
        const found = DATA.find((el) => el.id === c.symbol);
        return {
          ...c,
          image: found?.image,
        };
      })
      .filter((c) => !!c.image);
  }, [cryptocurrencies]);

  const chartData = useMemo(() => {
    if (!selected || !tokenPriceEod[selected.symbol]) {
      return [];
    }

    return tokenPriceEod[selected.symbol].map((item) => {
      const { timestamp, value } = item;
      return [moment(timestamp).valueOf(), value]; // Need to convert date string to timestamp: required by Highcharts
    });
  }, [selected, tokenPriceEod]);

  useEffect(() => {
    if (items?.length) {
      setSelected(items[0]);
    }
  }, [items]);

  useEffect(() => {
    if (!selected) return;
    const ids = [selected.symbol];
    const startDate = moment().add(-1, "years").format("YYYY-MM-DD"); // 1 year ago
    const endDate = moment().add(-1, "days").format("YYYY-MM-DD"); // Yesterday

    dispatch(
      fetchTokenPriceIndexEOD({
        assets: ids,
        startDate,
        endDate,
      })
    );
  }, [dispatch, selected]);

  const fetchMultiCalcData = useCallback(async () => {
    try {
      if (analyzerToken && calcInput) {
        const resp = await (parseInt(
          process.env.REACT_APP_USE_NEW_ANALYZER_API ?? "0",
          10
        ) > 0
          ? getMultiCalc(analyzerToken, calcInput)
          : getMultiCalcLegacy(analyzerToken, calcInput));
        if (resp) {
          setData(resp.result);
          setError(null);
        }
      }
    } catch (error: any) {
      console.error(error);
      setError(error);
    }
  }, [analyzerToken, calcInput]);

  useEffect(() => {
    fetchMultiCalcData();
  }, [fetchMultiCalcData]);

  useEffect(() => {
    if (error) {
      enqueueSnackbar(
        "Failed to fetch multi calculation data. Please try again later.",
        {
          variant: "error",
          autoHideDuration: 3000,
          anchorOrigin: {
            horizontal: "right",
            vertical: "top",
          },
          preventDuplicate: true,
        }
      );
    }
  }, [enqueueSnackbar, error]);

  return (
    <Box py={3}>
      <Carousel
        options={{
          infinite: true,
          slidesToShow: Math.min(12, items.length - 1),
          responsive: [
            {
              breakpoint: 1440,
              settings: {
                slidesToShow: Math.min(9, items.length - 1),
                slidesToScroll: 1,
              },
            },
            {
              breakpoint: 1024,
              settings: {
                slidesToShow: Math.min(8, items.length - 1),
                slidesToScroll: 1,
              },
            },
            {
              breakpoint: 600,
              settings: {
                slidesToShow: Math.min(6, items.length - 1),
                slidesToScroll: 2,
                initialSlide: 2,
              },
            },
            {
              breakpoint: 480,
              settings: {
                slidesToShow: Math.min(4, items.length - 1),
                slidesToScroll: 1,
              },
            },
          ],
        }}
      >
        {items.map((item) => (
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                paddingX: 2,
                cursor: "pointer",
              }}
              onClick={() => setSelected(item)}
            >
              <img src={item.image} alt={item.name} />
            </Box>
          </Box>
        ))}
      </Carousel>

      {selected ? (
        <CryptoOfferingCoin
          title={selected.name}
          description={selected?.description}
          chartData={chartData}
          multiCalcData={data}
        />
      ) : null}
    </Box>
  );
};

export default CryptoFeeds;
